import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { cn } from "@/utils/cn";

import SvgIcon from "../icon/svg-icon";
import Text from "../text/text";

const iconButtonVariants = cva(
  "flex items-center space-x-2 rounded-full border p-1 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-0",
  {
    variants: {
      variant: {
        primary:
          "border-transparent bg-transparent text-primary-500 hover:bg-primary-100 focus:bg-primary-200 dark:text-primary-400 dark:hover:bg-primary-700 dark:focus:bg-primary-800",
        outline:
          "border-gray-300 bg-transparent text-gray-900 hover:border-gray-500 dark:border-gray-800 dark:text-gray-100 dark:hover:border-gray-700",
        ghost:
          "border-transparent bg-transparent text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-800/50",
        accent:
          "border-transparent bg-secondary-500 text-gray-100 hover:bg-secondary-400 focus:bg-secondary-500 focus:ring-secondary-400",
        video:
          "border-0! text-white ring-0! ring-offset-0! transition-colors hover:bg-gray-900/25",
      },
    },
    defaultVariants: {
      variant: "ghost",
    },
  },
);

type IconButtonVariants = VariantProps<typeof iconButtonVariants>;
type IconButtonVariant = NonNullable<IconButtonVariants["variant"]>;

interface IconButtonProps extends ButtonPrimitive.Props, IconButtonVariants {
  /** Class name for the <svg> icon. */
  iconClassName?: string;
  /** URL to the svg icon. */
  src: string;
  /** Text to display next ot the button. */
  text?: string | React.JSX.Element;
  /** Override the data-testid */
  "data-testid"?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

/** A clickable icon. */
const IconButton = ({
  src,
  className,
  iconClassName,
  text,
  variant,
  type = "button",
  ...props
}: IconButtonProps) => (
  <ButtonPrimitive
    data-slot="icon-button"
    className={cn(iconButtonVariants({ variant }), className)}
    type={type}
    {...props}
    data-testid={props["data-testid"] || "icon-button"}
  >
    <div>
      <SvgIcon src={src} className={iconClassName} />
    </div>

    {text ? (
      <Text tag="span" theme="inherit" size="sm">
        {text}
      </Text>
    ) : null}
  </ButtonPrimitive>
);

export {
  IconButton as default,
  IconButton,
  iconButtonVariants,
  type IconButtonProps,
  type IconButtonVariant,
};
